import { Device } from "01Data/DataTypes";
import { Log } from "01Data/Log";
import { formatCellValue } from "99Tools/utils";
import { Backdrop, CircularProgress, styled, Typography } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { ja } from "date-fns/locale/ja";
import { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getFields } from "./logFields";

// 日本語ロケールを登録
registerLocale("ja", ja);

const ColorHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[800],
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  color: "#f00",
}));

/**
 * ログ一覧
 * @returns TSX
 */
const LogBody = ({ log }: { log: Log }) => {
  const rows = log.logItems
    .slice(
      log.page * log.rowsPerPage,
      log.page * log.rowsPerPage + log.rowsPerPage
    )
    .map((row: any, i: number) => {
      return (
        <TableRow hover tabIndex={-1} key={row.ItemId}>
          {getFields(log.kind).map((col) => (
            <TableCell key={`${col}_${i}`} align="center">
              <pre>{formatCellValue(col, row[col])}</pre>
            </TableCell>
          ))}
        </TableRow>
      );
    });

  return <TableBody>{rows}</TableBody>;
};

/**
 * ログテーブル
 * @returns TSX
 */
const LogTable = ({ log }: { log: Log }) => {
  const handleChangePage = (event: unknown, newPage: number) => {
    log.page = newPage;
    log.changed();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    log.rowsPerPage = +event.target.value;
    log.page = 0;
    log.changed();
  };

  const headers = getFields(log.kind).map((col) => (
    <ColorHeader key={col} align="center">
      {col}
    </ColorHeader>
  ));

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ minHeight: "55vh", maxHeight: "55vh" }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          size="small"
          sx={{ borderCollapse: "collapse" }}
        >
          <TableHead style={{ whiteSpace: "nowrap" }}>
            <TableRow>{headers}</TableRow>
          </TableHead>
          <LogBody log={log} />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={log.logItems.length}
        rowsPerPage={log.rowsPerPage}
        page={log.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

/**
 * ログ
 * @returns TSX
 */
export const LogIDSection = ({
  log,
  devices,
}: {
  log: Log;
  devices: Device[];
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    if (devices.length !== 0) {
      const sortedDevices = devices.sort((a, b) =>
        a.StatusUpdatedAt < b.StatusUpdatedAt ? 1 : -1
      );
      // 初期選択（更新日時降順で並べた先頭デバイス）
      log.deviceId = sortedDevices.length > 0 ? sortedDevices[0].id : "";
      const device = devices.filter((item: Device) => item.id === log.deviceId);
      log.kind = device[0].kind;
      const start = new Date().setDate(new Date().getDate() - 10 /*10日間前*/);
      log.start = new Date(start).toISOString().split("T")[0];
      log.end = new Date().toISOString().split("T")[0];
      log.page = 0;
      setPageLoading(true);
      log
        .GetLog()
        .catch((err: any) => {
          log.SetError(err, "ステータスログ取得失敗");
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [devices, log]);

  const update = () => {
    setErrorMessage("");
    setPageLoading(true);
    log
      .GetLog()
      .catch((err: any) => {
        log.SetError(err, "ステータスログ取得失敗");
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const handeChangeSensor = (e: any) => {
    log.deviceId = e.target.value;
    const device = devices.filter((item: Device) => item.id === log.deviceId);
    log.kind = device[0].kind;
    log.page = 0;
    update();
  };

  const handleDateChange = (date: any) => {
    const formatDate = (d: Date | null) => {
      return d
        ? `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`
        : "";
    };
    log.start = formatDate(date[0]);
    log.end = formatDate(date[1]);
    log.page = 0;
    update();
    setErrorMessage(date[1] ? "" : "終了日を選択してください。");
  };

  const pickerDate = (date: string) => {
    return date ? new Date(date) : null;
  };

  const oSensors = devices
    .sort((a, b) => (a.StatusUpdatedAt < b.StatusUpdatedAt ? 1 : -1))
    .map((item: Device) => {
      return (
        <MenuItem value={item.id} key={item.id}>
          <div>{item.name}</div>
        </MenuItem>
      );
    });

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <Select
          size="small"
          labelId="select_sensor_label"
          id="select_sensor_id"
          value={log.deviceId}
          onChange={handeChangeSensor}
          sx={{
            width: "200px",
            paddingRight: "35px",
          }}
        >
          {oSensors}
        </Select>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            zIndex: 100,
          }}
        >
          <ErrorText>{errorMessage}</ErrorText>
          <DatePicker
            showIcon
            locale="ja"
            selected={pickerDate(log.start)}
            onChange={handleDateChange}
            dateFormatCalendar="yyyy年 MM月"
            dateFormat="yyyy/MM/dd"
            startDate={pickerDate(log.start)}
            endDate={pickerDate(log.end)}
            selectsRange
            placeholderText=""
          />
        </div>
      </div>
      <LogTable log={log} />
      <Backdrop open={pageLoading} sx={{ zIndex: 2000 }}>
        <CircularProgress sx={{ color: "white" }} />
      </Backdrop>
    </>
  );
};
